import { useState, useEffect } from "react";
import axiosInstance from "../../redux/helpers/axios";
import Modal from "./Modal";
import CreateCampaignCategoryForm from "./CreateCampaignCategoryForm";

const ACCENT_FROM = "#E11D74";
const ACCENT_TO = "#EF4444";

export default function CategoriesPage() {
  const [campaigns, setCampaigns] = useState([]);
  const [campaignId, setCampaignId] = useState("");
  const [segments, setSegments] = useState([]);
  const [segmentId, setSegmentId] = useState("");
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState({
    campaigns: true,
    segments: false,
    categories: false,
  });
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    const fetchCampaigns = async () => {
      try {
        const res = await axiosInstance.get("/admin/campaign/get-all");
        const list = res.data.campaigns || [];
        setCampaigns(list);
        if (list.length > 0) setCampaignId(list[0].id);
      } catch (err) {
        setError(err?.response?.data?.error || "Failed to load campaigns");
      } finally {
        setLoading((prev) => ({ ...prev, campaigns: false }));
      }
    };
    fetchCampaigns();
  }, []);

  useEffect(() => {
    if (!campaignId) return;
    const fetchSegments = async () => {
      setLoading((prev) => ({ ...prev, segments: true }));
      try {
        const res = await axiosInstance.get(
          `/admin/campaign/segment/get-by-campaign/${campaignId}`,
        );
        const list = res.data.segments || [];
        setSegments(list);
        setSegmentId(list.length > 0 ? list[0].id : "");
      } catch (err) {
        setError(err?.response?.data?.error || "Failed to load segments");
      } finally {
        setLoading((prev) => ({ ...prev, segments: false }));
      }
    };
    fetchSegments();
  }, [campaignId]);

  const fetchCategories = async (id) => {
    setLoading((prev) => ({ ...prev, categories: true }));
    try {
      const res = await axiosInstance.get(
        `/admin/campaign/category/get-by-segment/${id}`,
      );
      setCategories(res.data.categories || []);
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to load categories");
    } finally {
      setLoading((prev) => ({ ...prev, categories: false }));
    }
  };

  useEffect(() => {
    if (segmentId) fetchCategories(segmentId);
    else setCategories([]);
  }, [segmentId]);

  const handleCreated = (category) => {
    setCategories((prev) => [category, ...prev]);
    setModalOpen(false);
  };

  return (
    <div className="max-w-5xl mx-auto p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Categories</h1>
          <p className="mt-1 text-sm text-gray-500">
            Categories within a segment.
          </p>
        </div>
        <button
          onClick={() => setModalOpen(true)}
          disabled={!segmentId}
          className="rounded-md px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
          style={{
            background: `linear-gradient(90deg, ${ACCENT_FROM}, ${ACCENT_TO})`,
          }}
        >
          + New category
        </button>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-4 max-w-lg">
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Campaign
          </label>
          <select
            value={campaignId}
            onChange={(e) => setCampaignId(e.target.value)}
            disabled={loading.campaigns}
            className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2"
            style={{ "--tw-ring-color": ACCENT_FROM }}
          >
            {loading.campaigns && <option>Loading...</option>}
            {!loading.campaigns && campaigns.length === 0 && (
              <option>No campaigns yet</option>
            )}
            {campaigns.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Segment
          </label>
          <select
            value={segmentId}
            onChange={(e) => setSegmentId(e.target.value)}
            disabled={loading.segments || segments.length === 0}
            className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 disabled:bg-gray-50"
            style={{ "--tw-ring-color": ACCENT_FROM }}
          >
            {loading.segments && <option>Loading...</option>}
            {!loading.segments && segments.length === 0 && (
              <option>No segments yet</option>
            )}
            {segments.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="mt-6 overflow-hidden rounded-lg border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Name
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {loading.categories && (
              <tr>
                <td className="px-4 py-6 text-center text-sm text-gray-400">
                  Loading...
                </td>
              </tr>
            )}
            {!loading.categories && error && (
              <tr>
                <td className="px-4 py-6 text-center text-sm text-red-600">
                  {error}
                </td>
              </tr>
            )}
            {!loading.categories && !error && categories.length === 0 && (
              <tr>
                <td className="px-4 py-6 text-center text-sm text-gray-400">
                  No categories yet.
                </td>
              </tr>
            )}
            {!loading.categories &&
              !error &&
              categories.map((cat) => (
                <tr key={cat.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">
                    {cat.name}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title="New category"
      >
        <CreateCampaignCategoryForm
          campaignSegmentId={segmentId}
          onCreated={handleCreated}
          onCancel={() => setModalOpen(false)}
        />
      </Modal>
    </div>
  );
}
